// Вкладки настроек и карточки в них — одно место для страницы, адреса (/settings?tab=…), поиска Ctrl+K и ссылок.
// Якорь карточки (#integrations, #claude-access, #browser…) сам открывает её вкладку и прокручивает к ней:
// так работают и ссылки из алертов api и worker (`/settings#…`), сохранённые в базе до появления вкладок.
import {
  Bot,
  CircleDollarSign,
  type LucideIcon,
  Plug,
  Server,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';

export const SETTINGS_TABS = [
  { id: 'money', label: 'Автономия и деньги', icon: CircleDollarSign },
  { id: 'protection', label: 'Страховка и антифрод', icon: ShieldCheck },
  { id: 'integrations', label: 'Интеграции', icon: Plug },
  { id: 'agent', label: 'Агент', icon: Bot },
  { id: 'general', label: 'Общие', icon: SlidersHorizontal },
  { id: 'system', label: 'Система', icon: Server },
] as const satisfies ReadonlyArray<{ id: string; label: string; icon: LucideIcon }>;

export type SettingsTab = (typeof SETTINGS_TABS)[number]['id'];

/** Без ?tab и без якоря — главное: деньги и свобода агента. */
export const DEFAULT_TAB: SettingsTab = 'money';

/**
 * Карточки в порядке показа внутри вкладки (сверху — важнее). id — якорь (#id) и id блока на странице;
 * find — слова для поиска Ctrl+K. Новая карточка — строка здесь и её вид в Settings.tsx (CARD_VIEW).
 */
export const SETTINGS_CARDS = [
  {
    id: 'autonomy',
    tab: 'money',
    // string, а не литерал: Ctrl+K (shell/Palette.tsx) сравнивает имя карточки с именем вкладки
    label: 'Бюджет и режим агента' as string,
    find: 'автономия деньги дневной бюджет маржа целевой окупаемый cpl режим агента запись в meta',
  },
  { id: 'gen', tab: 'money', label: 'Генерации для креативов', find: 'потолок на ролик fal gemini расходы' },
  { id: 'guard', tab: 'protection', label: 'Страховка', find: 'автостоп правила cpl расход без лидов' },
  { id: 'antifraud', tab: 'protection', label: 'Антифрод', find: 'риск задержка лидов боты' },
  { id: 'network', tab: 'integrations', label: 'Сеть и реклама', find: 'buyo meta постбек токен кабинеты' },
  {
    id: 'capi',
    tab: 'integrations',
    label: 'Апрувы в Meta',
    find: 'conversions api capi purchase выкуп пиксель тестовые события emq качество совпадения',
  },
  {
    id: 'services',
    tab: 'integrations',
    label: 'Сервисы',
    find: 'telegram бот gemini fal прокси cloudflare r2',
  },
  {
    id: 'panda-import',
    tab: 'integrations',
    label: 'Импорт из Panda-Manda',
    find: 'дамп phpmyadmin полные номера лиды старая панель',
  },
  { id: 'models', tab: 'agent', label: 'Модели агента', find: 'opus fable глубина размышлений' },
  { id: 'claude-access', tab: 'agent', label: 'Доступ к Claude', find: 'токен подписки setup-token' },
  { id: 'subscription', tab: 'agent', label: 'Подписка Claude', find: 'лимиты подписки' },
  { id: 'browser', tab: 'agent', label: 'Браузер агента', find: 'удалённый экран instagram tiktok youtube' },
  { id: 'push', tab: 'general', label: 'Уведомления на телефоне', find: 'push оповещения' },
  { id: 'install', tab: 'general', label: 'Приложение на телефоне', find: 'установить главный экран' },
  { id: 'login', tab: 'general', label: 'Вход', find: 'пароль ключ доступа выйти' },
  { id: 'appearance', tab: 'general', label: 'Внешний вид', find: 'тема тёмная светлая' },
  { id: 'backup', tab: 'system', label: 'Бэкапы и хранилище', find: 'копия базы cloudflare r2 диск медиа' },
  {
    id: 'ping',
    tab: 'system',
    label: 'Внешний пульс',
    find: 'healthchecks мониторинг сервер упал письмо',
  },
  { id: 'claude-code', tab: 'system', label: 'Claude Code', find: 'версия обновление проверка агента' },
  { id: 'dev', tab: 'system', label: 'Разработка', find: 'правки платформы релизы откат' },
] as const satisfies ReadonlyArray<{ id: string; tab: SettingsTab; label: string; find: string }>;

export type SettingsCardId = (typeof SETTINGS_CARDS)[number]['id'];

/** Строки внутри карточек, на которые ведут ссылки (#telegram — привязать бота для кода входа). */
const ROWS: Record<string, SettingsTab> = {
  buyo: 'integrations',
  meta: 'integrations',
  telegram: 'integrations',
  gemini: 'integrations',
  fal: 'integrations',
  proxy: 'integrations',
  r2: 'integrations',
};

export function isSettingsTab(x: unknown): x is SettingsTab {
  return SETTINGS_TABS.some((t) => t.id === x);
}

/** Вкладка по якорю: имя вкладки (#integrations), карточки (#claude-access) или строки (#telegram). */
export function tabOfAnchor(hash: string): SettingsTab | undefined {
  const id = hash.replace(/^#/, '');
  if (!id) return undefined;
  if (isSettingsTab(id)) return id;
  return SETTINGS_CARDS.find((c) => c.id === id)?.tab ?? ROWS[id];
}
